import React from 'react';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaCircle, FaClock, FaRocket } from 'react-icons/fa';
import './Timeline.css';

const milestones = [
  {
    id: 'formation',
    period: '2018',
    title: 'Company Formation',
    description:
      'Antaral Aerospace founded. Core team assembled around a shared conviction: the new space economy needs launch technology that is faster, leaner, and more accessible.',
    status: 'done',
    icon: FaCheckCircle
  },
  {
    id: 'architecture',
    period: '2024–2025',
    title: 'Architecture & Systems Design',
    description:
      'RPSLV and RMLV vehicle architecture defined. Mission profiles, payload requirements, and systems-level trade studies underway. First CubeSat program concepts formalized.',
    status: 'active',
    icon: FaCircle
  },
  {
    id: 'propulsion',
    period: '2025–2026',
    title: 'Engine & Propulsion Development',
    description:
      'Propulsion research in progress. Engine cycle selection, preliminary thrust chamber design, and computational fluid dynamics analysis underway.',
    status: 'active',
    icon: FaCircle
  },
  {
    id: 'prototype',
    period: '2026',
    title: 'Sub-system Prototyping',
    description:
      'Component-level prototyping across avionics, structures, and flight software. Environmental test campaigns begin. Integration procedures established.',
    status: 'planned',
    icon: FaClock
  },
  {
    id: 'ground-test',
    period: '2027',
    title: 'Ground Testing Campaign',
    description:
      'Static fire tests, structural qualification, full vehicle ground integration, and flight readiness reviews. Rideshare booking portal soft launch.',
    status: 'planned',
    icon: FaClock
  },
  {
    id: 'first-flight',
    period: '2028+',
    title: 'First Flight Demonstration',
    description:
      'First RPSLV demonstration mission targeting LEO rideshare deployment. Beginning of commercial rideshare operations and public mission booking.',
    status: 'future',
    icon: FaRocket
  }
];

const statusLabel = {
  done: 'Achieved',
  active: 'In progress',
  planned: 'Planned',
  future: 'Future target'
};

const sectionVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.09, delayChildren: 0.05 }
  }
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] }
  }
};

const milestoneVariants = {
  hidden: { opacity: 0, x: -24 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

const Timeline = () => (
  <section id="timeline" className="timeline-section" aria-labelledby="timeline-heading">
    <div className="timeline-section__container">
      <motion.div
        className="timeline-section__intro"
        variants={sectionVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-80px' }}
      >
        <motion.span className="section-kicker" variants={fadeUp}>Roadmap</motion.span>
        <motion.h2 id="timeline-heading" className="section-heading" variants={fadeUp}>
          From foundation to <span>first flight.</span>
        </motion.h2>
        <motion.p className="section-copy" variants={fadeUp}>
          We are building with deliberate, engineering-first pacing. Every milestone is earned
          before moving to the next — no promises, just progress.
        </motion.p>
      </motion.div>

      <div className="timeline-section__track">
        <div className="timeline-section__line" aria-hidden="true" />

        <motion.div
          className="timeline-section__milestones"
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
        >
          {milestones.map((m, index) => {
            const Icon = m.icon;
            return (
              <motion.div
                key={m.id}
                className={`timeline-item timeline-item--${m.status} ${index % 2 === 0 ? 'timeline-item--left' : 'timeline-item--right'}`}
                variants={milestoneVariants}
              >
                <div className={`timeline-item__node timeline-item__node--${m.status}`} aria-hidden="true">
                  <Icon />
                </div>

                <motion.div
                  className="timeline-item__card glass-card"
                >
                  <div className="timeline-item__card-top">
                    <time className="timeline-item__period">{m.period}</time>
                    <span className={`timeline-item__status timeline-item__status--${m.status}`}>
                      {statusLabel[m.status]}
                    </span>
                  </div>
                  <h3 className="timeline-item__title">{m.title}</h3>
                  <p className="timeline-item__desc">{m.description}</p>
                </motion.div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </div>
  </section>
);

export default Timeline;
